import StopDestinationRefinement from '../StopDestinationRefinement'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import zIndex from 'styles/tools/z-index'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import type { TripStop } from 'tripPlanner/api/types'
import TripListLuxPlusToggle from './TripListLuxPlusToggle'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import StopSearchActions from './StopSearchActions'
import LocationFilterMenuItem from './LocationFilterMenuItem'
import TripListRowItem from './TripListRowItem'
import StatusFilterMenuItem from './StatusFilterMenuItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import { TRIP_LIST_MIN_WIDTH } from './tripListLayout'
import {
  canMoveTripListRowToAnotherDay,
  getTripListBulkMoveDayOptions,
  type TripListMoveDayOption,
} from './tripListMoveDayOptions'
import TripListQuickActions from './TripListQuickActions'
import { getTripListTypeLabel } from './tripListTypeLabels'
import { getTripListFooterTotalPrice } from './tripListPrices'
import { getTripListStatusLabel } from './tripListStatus'
import StopTimingEditControl from '../StopTimingEditControl'
import {
  getTripListDateFilterOptions,
  getTripListLocationFilterOptions,
  getTripListRowCountByStatus,
  getTripListRowCountByType,
  getTripListStatusFilterOptions,
  getTripListTypeFilterOptions,
  hasTripListBookableOnlyFilterOption,
  hasTripListDateFilterOptions,
  hasTripListLocationFilterOptions,
  hasTripListStatusFilterOptions,
  hasTripListTypeFilterOptions,
} from './tripListFilterOptions'
import { useCloseDropdownWhenUnavailable } from './useCloseDropdownWhenUnavailable'
import type { ItineraryScrollTarget } from '../../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../../hooks/useItineraryScrollTarget'
import WhileDraggingStyle from 'tripPlanner/components/DragAndDrop/WhileDraggingStyle'
import { DROP_ANIMATION_DURATION, autoScrollConfig, dropAnimationSideEffects, type TripListRenderableGroup } from './tripListReorder'
import { useTripListDragReorder } from './useTripListDragReorder'
import { isBookableTripListRow } from './tripListRows'
import { drawerBodyContentPaddingStyles } from '../drawerBodyLayout.styles'
import type {
  TripListFilters,
  TripListGroup,
  TripListRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
  TripListStatusValue,
} from './tripListTypes'

const TRIP_LIST_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

const ListPanel = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const Toolbar = styled.div`
  /* Trip Kit 3.0: the filter row stays pinned to the top of the scroll area as
     the itinerary scrolls under it. z-index clears the day/stop headers; the
     opaque background stops rows showing through. */
  position: sticky;
  top: 0;
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${rem(16)};
  width: 100%;
  /* Track the content stack's own minimum so the pinned row still spans the
     full table when the panel is narrow enough to scroll it horizontally. */
  min-width: ${TRIP_LIST_MIN_WIDTH};
  /* Trip Kit 3.0: 16px above and below the filter row, and no rule under it --
     the gap to the first stop card is the only separation in the design. */
  padding: ${rem(16)} 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  flex-shrink: 0;
`

const FilterChips = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: ${rem(8)};
  min-width: 0;
  overflow: visible;
`

const ClearFiltersButton = styled(TextButton)`
  && {
    height: ${rem(32)};
    padding-inline: ${rem(10)};
    border-radius: ${props => props.theme.borderRadius.round};
  }
`

const ScrollArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 0 ${rem(16)} ${rem(16)};
`

const FilterChipButton = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  min-width: ${rem(72)};
  height: ${rem(32)};
  padding: 0 ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &:disabled,
  &:disabled:hover {
    cursor: default;
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    svg {
      color: currentColor;
    }
  }
`

const FilterChipText = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-weight: 500;
`

const FilterDropdownList = styled(DropdownList)`
  > div > div {
    padding-bottom: 0;
  }
`

const TypeFilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: ${rem(256)};
  max-height: ${rem(360)};
`

const TypeFilterHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: ${rem(4)} ${rem(16)} ${rem(2)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const TypeFilterContent = styled.div`
  overflow-y: auto;
  padding: 0 ${rem(16)};
`

const TypeFilterOptions = styled.div`
  margin-top: ${rem(4)};
`

const TypeFilterFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(16)};
  padding: ${rem(16)} ${rem(16)} 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const ClearFilterButton = styled(TextButton)`
  min-width: 0;
  padding-inline: 0;
`

const DragOverlayContent = styled.div`
  opacity: 0.68;
  pointer-events: none;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  width: 100%;
  min-width: ${TRIP_LIST_MIN_WIDTH};
`

/*
 * Trip Kit 3.0 renders each stop as one bordered card (Figma `Table`): the stop
 * header, then a white band per day. The card's own sand background is what
 * shows through the 16px gaps between those bands, so the border and radius
 * wrap the whole stop rather than each day.
 */
const StopCard = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const DayBody = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  & + & {
    margin-top: ${rem(16)};
  }

  /*
   * The 16px sand band between two days is ruled on both edges. Those rules sit
   * on the row content cells -- not the full width -- so they line up with the
   * separators between rows and clear the selection/date gutters, exactly as the
   * Figma .TableRow borders do.
   */
  & + & > *:first-child .is-row-content-cell {
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &:not(:last-child) > *:last-child .is-row-content-cell {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const StopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(16)};
  min-height: ${rem(56)};
  padding: ${rem(12)} ${rem(16)};
  /*
   * Trip Kit 3.0 retires the stop-header band entirely: the header shares the
   * white row surface and is separated from the first row by a single rule.
   */
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const StopHeaderText = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: ${rem(12)};
`

const StopHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  margin-inline-start: auto;
  flex-shrink: 0;
`

const StopHeaderTitle = styled(BodyText)`
  min-width: 0;
  flex: 0 1 auto;
  overflow-wrap: anywhere;
`

const StopHeaderSubtitle = styled(BodyText)`
  min-width: 0;
  flex: 1 0 auto;
  color: inherit;
`

/*
 * Figma `Stop_EmptyState`: a single 44px white row of copy. The stop's search
 * buttons stay in the header, so nothing is repeated down here.
 */
const EmptyStopPlaceholder = styled.div`
  display: flex;
  align-items: center;
  min-height: ${rem(44)};
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  margin: 0;
  padding: ${rem(16)} ${rem(20)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  min-width: 0;
  flex-shrink: 0;
`

const FooterSummary = styled(Group)`
  min-width: 0;
`

const FooterActions = styled(Group)`
  flex-shrink: 0;
`

const CountChip = styled.span`
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(200)};
  ${drawerBodyContentPaddingStyles}
`

const BookAllButton = styled(TextButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

/**
 * One stop's worth of the list: the header, a band of rows per day, and the
 * empty-stop line. Trip Kit 3.0 draws all of that inside a single card, but the
 * group model hands us one entry per *day*, with the stop header only ever set
 * on a stop's first group. Consecutive groups belonging to the same stop are
 * folded back together here.
 */
interface TripListStopCard {
  id: string
  stopHeader: TripListGroup['stopHeader']
  stopSearchActions: TripListGroup['stopSearchActions']
  dayGroups: Array<TripListRenderableGroup>
  emptyGroup: TripListRenderableGroup | undefined
}

function getTripListRenderableGroupStopId(group: TripListRenderableGroup): TripStop['id'] | undefined {
  return group.group.stopHeader?.stop.id ?? group.rows[0]?.row.stop.id
}

export function buildTripListStopCards(
  groups: Array<TripListRenderableGroup>,
): Array<TripListStopCard> {
  const cards: Array<TripListStopCard> = []
  let current: TripListStopCard | undefined
  let currentStopId: TripStop['id'] | undefined

  for (const group of groups) {
    const hasContent = group.rows.length > 0 || group.group.showEmptyPlaceholder === true || group.group.stopHeader !== undefined
    if (!hasContent) continue

    const stopId = getTripListRenderableGroupStopId(group)
    // A group that carries the stop header always opens a card. Otherwise a
    // group joins the open card only while it belongs to the same stop -- an
    // unattributed group (mid-drag, say) gets its own so it can never be filed
    // under the wrong header.
    const startsCard = current === undefined ||
      group.group.stopHeader !== undefined ||
      stopId === undefined ||
      stopId !== currentStopId

    if (startsCard) {
      current = {
        id: group.id,
        stopHeader: group.group.stopHeader,
        stopSearchActions: group.group.stopSearchActions,
        dayGroups: [],
        emptyGroup: undefined,
      }
      cards.push(current)
      currentStopId = stopId
    }

    if (group.group.showEmptyPlaceholder && !current!.stopHeader?.stop.destinationRefinementPrompt?.trim()) current!.emptyGroup = group
    if (group.rows.length > 0) current!.dayGroups.push(group)
  }

  return cards
}

export interface TripListViewProps {
  groups: Array<TripListGroup>
  tripStops: Array<TripStop>
  allRows: Array<TripListRow>
  visibleRows: Array<TripListRow>
  selectedRows: Array<TripListRow>
  selectedRowIds: Set<string>
  filters: TripListFilters
  sort: TripListSort | undefined
  luxPlusEnabled: boolean
  onSelectAll: (checked: boolean) => void
  onSelectRow: (rowId: string, checked: boolean) => void
  onDateFiltersChange: (dates: Set<string>) => void
  onLocationFiltersChange: (locations: Set<string>) => void
  onTypeFiltersChange: (types: Set<TripListRowType>, bookableOnly: boolean) => void
  onStatusFiltersChange: (statuses: Set<TripListStatusValue>) => void
  // Fired when any filter panel opens/closes (desktop: one of four independent dropdowns; mobile:
  // the one shared filters modal) so the activity dispatch for a filter change can happen once per
  // close, describing everything that changed while it was open, not once per checkbox toggle.
  // Optional with a noop default (CLAUDE.md convention) rather than required -- every real call
  // site passes both, but a test-only render helper that omits them (invisible to the type gate,
  // since typecheck.tsconfig.json excludes test files) should no-op instead of crashing.
  onFilterPanelOpened?: () => void
  onFilterPanelClosed?: () => void
  onClearFilters: () => void
  onSortToggle: (field: TripListSortField) => void
  onLuxPlusToggle: () => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onBookAll: () => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiMove: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiSelectedRows: (origin?: { x: number; y: number }) => void
  onAskAiMoveSelectedRows: (origin?: { x: number; y: number }) => void
  onDelete: (row: TripListRow) => Promise<void>
  onMoveToDay: (row: TripListRow, option: TripListMoveDayOption) => void | boolean | Promise<void | boolean>
  onMoveSelectedRowsToDay: (option: TripListMoveDayOption) => void | Promise<void>
  onDeleteSelectedRows: () => Promise<void>
  onClearSelection: () => void
  onOpenStopHotels: (stop: TripStop) => void
  onOpenStopFlights: (stop: TripStop) => void
  onOpenStopExperiences: (stop: TripStop) => void
  onSendStopTimingEditMessage: (message: string) => Promise<void> | void
  onUpdateNote: (row: TripListRow, notes: string | undefined) => void
  onUpdateStatus: (row: TripListRow, status: TripListStatusValue) => void
  bookingRowId?: string
  noteUpdateRowId?: string
  statusUpdateRowId?: string
  notesDisabled?: boolean
  statusDisabled?: boolean
  actionsDisabled?: boolean
  scrollTarget?: ItineraryScrollTarget
  onScrollComplete?: () => void
}

function getSortIcon(sort: TripListSort | undefined, field: TripListSortField) {
  if (sort?.field !== field) return <LineAngleDownIcon size="XS" />
  return sort.direction === 'asc' ? <LineArrowUpIcon size="XS" /> : <LineArrowDownIcon size="XS" />
}

function TripListView({
  groups,
  tripStops,
  allRows,
  visibleRows,
  selectedRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectRow,
  onDateFiltersChange,
  onLocationFiltersChange,
  onTypeFiltersChange,
  onFilterPanelOpened = noop,
  onFilterPanelClosed = noop,
  onStatusFiltersChange,
  onClearFilters,
  onSortToggle,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onBookAll,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onAskAiSelectedRows,
  onAskAiMoveSelectedRows,
  onDelete,
  onMoveToDay,
  onMoveSelectedRowsToDay,
  onDeleteSelectedRows,
  onClearSelection,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
  onSendStopTimingEditMessage,
  onUpdateNote,
  onUpdateStatus,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  scrollTarget,
  onScrollComplete,
}: TripListViewProps) {
  const intl = useIntl()
  const [locationFilterOpen, setLocationFilterOpen] = useState(false)
  const [dateFilterOpen, setDateFilterOpen] = useState(false)
  const [typeFilterOpen, setTypeFilterOpen] = useState(false)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false)
  const dateFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const locationFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const typeFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const statusFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollAreaRef,
    selectors: TRIP_LIST_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const bookableRows = useMemo(() => visibleRows.filter(isBookableTripListRow), [visibleRows])
  const hasVisibleRows = visibleRows.length > 0
  const footerTotal = useMemo(() => getTripListFooterTotalPrice(bookableRows.map(row => row.price), luxPlusEnabled), [bookableRows, luxPlusEnabled])
  const selectedRowsCanMoveToDay = selectedRows.length > 0 && selectedRows.every(canMoveTripListRowToAnotherDay)
  const selectedMoveDayOptions = useMemo(
    () => selectedRowsCanMoveToDay ? getTripListBulkMoveDayOptions(selectedRows, tripStops, intl) : [],
    [intl, selectedRows, selectedRowsCanMoveToDay, tripStops],
  )
  const showSelectedMoveWithAi = selectedRows.length > 0 && !selectedRowsCanMoveToDay
  const activeDateFilterCount = filters.dates.size
  const hasActiveDateFilter = activeDateFilterCount > 0
  const activeLocationFilterCount = filters.locations.size
  const hasActiveLocationFilter = activeLocationFilterCount > 0
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const hasActiveTypeFilter = activeTypeFilterCount > 0
  const activeStatusFilterCount = filters.statuses.size
  const hasActiveStatusFilter = activeStatusFilterCount > 0
  const hasActiveSort = Boolean(sort)
  const hasActiveFilters = hasActiveDateFilter || hasActiveLocationFilter || hasActiveTypeFilter || hasActiveStatusFilter || filters.bookableOnly || hasActiveSort
  const dragActionsDisabled = actionsDisabled || hasActiveFilters
  const {
    orderedRenderableGroups,
    activeRenderableRow,
    overDragRowId,
    dropTargetPlacement,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useTripListDragReorder({
    groups,
    visibleRows,
    selectedRowIds,
    bookingRowId,
    noteUpdateRowId,
    statusUpdateRowId,
    dragActionsDisabled,
    onMoveToDay,
  })
  const stopCards = useMemo(() => buildTripListStopCards(orderedRenderableGroups), [orderedRenderableGroups])
  const dateFilterOptions = useMemo(() => getTripListDateFilterOptions(allRows), [allRows])
  const hasDateFilterOptions = hasTripListDateFilterOptions(allRows)
  const locationFilterOptions = useMemo(() => getTripListLocationFilterOptions(allRows), [allRows])
  const hasLocationFilterOptions = hasTripListLocationFilterOptions(allRows)
  const rowCountByType = useMemo(() => getTripListRowCountByType(allRows), [allRows])
  const typeFilterOptions = useMemo(() => getTripListTypeFilterOptions(rowCountByType), [rowCountByType])
  const hasTypeFilterOptions = hasTripListTypeFilterOptions(allRows)
  const hasBookableOnlyFilterOption = hasTripListBookableOnlyFilterOption(allRows)
  const rowCountByStatus = useMemo(() => getTripListRowCountByStatus(allRows), [allRows])
  const statusFilterOptions = useMemo(() => getTripListStatusFilterOptions(rowCountByStatus), [rowCountByStatus])
  const hasStatusFilterOptions = hasTripListStatusFilterOptions(allRows)
  const addDaysOrDatesLabel = intl.formatMessage({
    id: 'tCy9PycI1Guoepw6mk8q',
    defaultMessage: 'Add days or dates',
    description: 'Action text shown on a trip planner stop with no dates or day count',
  })
  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  // Reads `dateFilterOpen` directly rather than deriving `next` inside the setState updater --
  // updaters must be pure (React may invoke one more than once, e.g. under StrictMode), and the
  // updater form was firing onFilterPanelOpened() on every such replay. Also covers closing by
  // re-clicking the trigger chip: LuxKit's dropdown deliberately does not fire its own onClose for
  // a click on the trigger element (only for a click elsewhere), so closeDateFilter below was never
  // reached that way -- toggling straight off the open/close transition here catches both paths.
  const toggleDateFilter = useCallback(() => {
    if (!hasDateFilterOptions) return
    if (dateFilterOpen) {
      onFilterPanelClosed()
    } else {
      onFilterPanelOpened()
    }
    setDateFilterOpen(!dateFilterOpen)
  }, [hasDateFilterOptions, dateFilterOpen, onFilterPanelOpened, onFilterPanelClosed])

  const closeDateFilter = useCallback(() => {
    setDateFilterOpen(false)
    onFilterPanelClosed()
  }, [onFilterPanelClosed])

  const handleDateFilterToggle = useCallback((date: string) => {
    const next = new Set(filters.dates)
    if (next.has(date)) {
      next.delete(date)
    } else {
      next.add(date)
    }
    onDateFiltersChange(next)
  }, [filters.dates, onDateFiltersChange])

  const handleClearDateFilters = useCallback(() => {
    onDateFiltersChange(new Set())
  }, [onDateFiltersChange])

  // See toggleDateFilter above for why this reads open state directly rather than deriving it
  // inside the setState updater.
  const toggleLocationFilter = useCallback(() => {
    if (!hasLocationFilterOptions) return
    if (locationFilterOpen) {
      onFilterPanelClosed()
    } else {
      onFilterPanelOpened()
    }
    setLocationFilterOpen(!locationFilterOpen)
  }, [hasLocationFilterOptions, locationFilterOpen, onFilterPanelOpened, onFilterPanelClosed])

  const closeLocationFilter = useCallback(() => {
    setLocationFilterOpen(false)
    onFilterPanelClosed()
  }, [onFilterPanelClosed])

  const handleLocationFilterToggle = useCallback((location: string) => {
    const next = new Set(filters.locations)
    if (next.has(location)) {
      next.delete(location)
    } else {
      next.add(location)
    }
    onLocationFiltersChange(next)
  }, [filters.locations, onLocationFiltersChange])

  const handleClearLocationFilters = useCallback(() => {
    onLocationFiltersChange(new Set())
  }, [onLocationFiltersChange])

  // See toggleDateFilter above for why this reads open state directly rather than deriving it
  // inside the setState updater.
  const toggleTypeFilter = useCallback(() => {
    if (!hasTypeFilterOptions) return
    if (typeFilterOpen) {
      onFilterPanelClosed()
    } else {
      onFilterPanelOpened()
    }
    setTypeFilterOpen(!typeFilterOpen)
  }, [hasTypeFilterOptions, typeFilterOpen, onFilterPanelOpened, onFilterPanelClosed])

  const closeTypeFilter = useCallback(() => {
    setTypeFilterOpen(false)
    onFilterPanelClosed()
  }, [onFilterPanelClosed])

  const handleTypeFilterToggle = useCallback((type: TripListRowType) => {
    const next = new Set(filters.types)
    if (next.has(type)) {
      next.delete(type)
    } else {
      next.add(type)
    }
    onTypeFiltersChange(next, filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleBookableOnlyToggle = useCallback(() => {
    onTypeFiltersChange(new Set(filters.types), !filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleClearTypeFilters = useCallback(() => {
    onTypeFiltersChange(new Set(), false)
  }, [onTypeFiltersChange])

  // See toggleDateFilter above for why this reads open state directly rather than deriving it
  // inside the setState updater.
  const toggleStatusFilter = useCallback(() => {
    if (!hasStatusFilterOptions) return
    if (statusFilterOpen) {
      onFilterPanelClosed()
    } else {
      onFilterPanelOpened()
    }
    setStatusFilterOpen(!statusFilterOpen)
  }, [hasStatusFilterOptions, statusFilterOpen, onFilterPanelOpened, onFilterPanelClosed])

  const closeStatusFilter = useCallback(() => {
    setStatusFilterOpen(false)
    onFilterPanelClosed()
  }, [onFilterPanelClosed])

  const handleStatusFilterToggle = useCallback((status: TripListStatusValue) => {
    const next = new Set(filters.statuses)
    if (next.has(status)) {
      next.delete(status)
    } else {
      next.add(status)
    }
    onStatusFiltersChange(next)
  }, [filters.statuses, onStatusFiltersChange])

  const handleClearStatusFilters = useCallback(() => {
    onStatusFiltersChange(new Set())
  }, [onStatusFiltersChange])

  const handleClearAllFilters = useCallback(() => {
    setDateFilterOpen(false)
    setLocationFilterOpen(false)
    setTypeFilterOpen(false)
    setStatusFilterOpen(false)
    onClearFilters()
  }, [onClearFilters])

  const renderTotalAmount = useCallback((chunks: ReactNode) => (
    <BodyText as="strong" variant="large" weight="bold">{chunks}</BodyText>
  ), [])

  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const typeHeaderLabel = intl.formatMessage({
    id: 'RTLU_2os7TlW4Bu0fGMO',
    defaultMessage: 'Type',
    description: 'Type column header label in the trip planner list view',
  })

  const dateFilterLabel = intl.formatMessage({
    id: 'aas_-qQnld7hAbKqjrO4',
    defaultMessage: 'Date',
    description: 'Date filter chip label in the trip planner list view',
  })

  const locationFilterLabel = intl.formatMessage({
    id: 'X3S-4N1Qdxx3efgejC7X',
    defaultMessage: 'Location',
    description: 'Location column header label in the trip planner list view',
  })

  const statusFilterLabel = intl.formatMessage({
    id: 'BsOr1mxehY4HiiOzz7Jf',
    defaultMessage: 'Status',
    description: 'Status column header label in the trip planner list view',
  })

  const priceFilterLabel = intl.formatMessage({
    id: 'ChJyqLBRZb8uxapP1Izz',
    defaultMessage: 'Price',
    description: 'Price column header label in the trip planner list view',
  })

  // The instrumented close*Filter wrappers, not the raw setters -- an itinerary change that makes
  // a filter's own options disappear while its panel is open (e.g. deleting the only Tokyo row
  // while the location panel is open) is a real change same as any other close, and needs the same
  // onFilterPanelClosed dispatch. The no-op gate inside buildListFilterChangedActivityEntry already
  // covers the (more common) case where the panel wasn't actually open, so this is safe to call
  // unconditionally on every "options became unavailable" transition. Passed directly (not wrapped
  // in a new inline arrow, which would change identity every render and re-run the hook's effect
  // on every render while hasOptions stays false) -- each close*Filter is already its own stable
  // useCallback, and TS allows a `() => void` where `(open: boolean) => void` is expected.
  useCloseDropdownWhenUnavailable(hasDateFilterOptions, closeDateFilter)
  useCloseDropdownWhenUnavailable(hasLocationFilterOptions, closeLocationFilter)
  useCloseDropdownWhenUnavailable(hasTypeFilterOptions, closeTypeFilter)
  useCloseDropdownWhenUnavailable(hasStatusFilterOptions, closeStatusFilter)

  return (
    <ListPanel data-testid="trip-list-desktop-view">
      <ScrollArea ref={scrollAreaRef}>
        <Toolbar data-testid="trip-list-desktop-toolbar">
          <FilterChips>
            {hasDateFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleDateFilter}
                  ref={dateFilterTriggerRef}
                  className={hasActiveDateFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{dateFilterLabel}</FilterChipText>
                  {hasActiveDateFilter && <Caption variant="medium" weight="bold">{activeDateFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={dateFilterOpen}
                  onClose={closeDateFilter}
                  triggerRef={dateFilterTriggerRef}
                  anchorRef={dateFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {dateFilterOptions.map(({ value, label, count }) => (
                          <LocationFilterMenuItem
                            key={value}
                            value={value}
                            label={label}
                            count={count}
                            selected={filters.dates.has(value)}
                            onToggle={handleDateFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearDateFilters}>
                        {intl.formatMessage({
                          id: 'oXyAbTonqCuJBi5AF7V3',
                          defaultMessage: 'Reset',
                          description: 'Button label for clearing one filter group dropdown in the trip planner list view',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasLocationFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleLocationFilter}
                  ref={locationFilterTriggerRef}
                  className={hasActiveLocationFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{locationFilterLabel}</FilterChipText>
                  {hasActiveLocationFilter && <Caption variant="medium" weight="bold">{activeLocationFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={locationFilterOpen}
                  onClose={closeLocationFilter}
                  triggerRef={locationFilterTriggerRef}
                  anchorRef={locationFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {locationFilterOptions.map(({ location, count }) => (
                          <LocationFilterMenuItem
                            key={location.filterValue}
                            value={location.filterValue}
                            label={location.label}
                            count={count}
                            selected={filters.locations.has(location.filterValue)}
                            onToggle={handleLocationFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearLocationFilters}>
                        {intl.formatMessage({
                          id: 'oXyAbTonqCuJBi5AF7V3',
                          defaultMessage: 'Reset',
                          description: 'Button label for clearing one filter group dropdown in the trip planner list view',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasTypeFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleTypeFilter}
                  ref={typeFilterTriggerRef}
                  className={hasActiveTypeFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{typeHeaderLabel}</FilterChipText>
                  {hasActiveTypeFilter && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={typeFilterOpen}
                  onClose={closeTypeFilter}
                  triggerRef={typeFilterTriggerRef}
                  anchorRef={typeFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {typeFilterOptions.map(type => (
                          <TypeFilterMenuItem
                            key={type}
                            type={type}
                            label={typeLabelByType[type]}
                            count={rowCountByType[type]}
                            selected={filters.types.has(type)}
                            onToggle={handleTypeFilterToggle}
                          />
                        ))}
                        {hasBookableOnlyFilterOption && (
                          <LocationFilterMenuItem
                            value="bookableOnly"
                            label={intl.formatMessage({
                              id: 'Z0lGZ1R9f0cNfrErpwD4',
                              defaultMessage: 'Bookable only',
                              description: 'Filter option label for showing only bookable rows in the trip planner list view',
                            })}
                            count={bookableRows.length}
                            selected={filters.bookableOnly}
                            onToggle={handleBookableOnlyToggle}
                          />
                        )}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearTypeFilters}>
                        {intl.formatMessage({
                          id: 'oXyAbTonqCuJBi5AF7V3',
                          defaultMessage: 'Reset',
                          description: 'Button label for clearing one filter group dropdown in the trip planner list view',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasStatusFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleStatusFilter}
                  ref={statusFilterTriggerRef}
                  className={hasActiveStatusFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{statusFilterLabel}</FilterChipText>
                  {hasActiveStatusFilter && <Caption variant="medium" weight="bold">{activeStatusFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={statusFilterOpen}
                  onClose={closeStatusFilter}
                  triggerRef={statusFilterTriggerRef}
                  anchorRef={statusFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {statusFilterOptions.map(status => (
                          <StatusFilterMenuItem
                            key={status}
                            status={status}
                            label={getTripListStatusLabel(intl, status)}
                            count={rowCountByStatus[status]}
                            selected={filters.statuses.has(status)}
                            onToggle={handleStatusFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearStatusFilters}>
                        {intl.formatMessage({
                          id: 'oXyAbTonqCuJBi5AF7V3',
                          defaultMessage: 'Reset',
                          description: 'Button label for clearing one filter group dropdown in the trip planner list view',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            <FilterChipButton
              type="button"
              onClick={handlePriceSortClick}
              className={sort?.field === 'price' ? 'is-active' : undefined}
              disabled={!hasVisibleRows}
              aria-label={intl.formatMessage({
                id: 'Nh7mkoTbbVKMqi-doe60',
                defaultMessage: 'Sort by price',
                description: 'Accessible label for the trip planner list view price column sort button',
              })}
            >
              <FilterChipText>{priceFilterLabel}</FilterChipText>
              {getSortIcon(sort, 'price')}
            </FilterChipButton>
            <TripListLuxPlusToggle
              checked={luxPlusEnabled}
              onChange={onLuxPlusToggle}
              disabled={!hasVisibleRows}
            />
            {hasActiveFilters && (
              <ClearFiltersButton
                size="medium"
                kind="secondary"
                startIcon={<LineTimesIcon size="S" />}
                onClick={handleClearAllFilters}
              >
                {intl.formatMessage({
                  id: '8DWxUcUpXrymuLxk9AsR',
                  defaultMessage: 'Reset all',
                  description: 'Toolbar button that clears every filter and sort in the trip planner list view',
                })}
              </ClearFiltersButton>
            )}
          </FilterChips>
        </Toolbar>
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '_HhYADAgKgwTYQp743JX',
                defaultMessage: 'No itinerary items match these filters.',
                description: 'Empty state shown when the trip planner list view has no rows after filtering',
              })}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            autoScroll={autoScrollConfig}
          >
            <ContentStack>
              {stopCards.map(card => (
                <StopCard key={card.id}>
                  {card.stopHeader && (
                    <StopHeader
                      data-testid="trip-list-desktop-stop-header"
                      data-stop-id={card.stopHeader.stop.id}
                      data-trip-list-group-target-id={card.id}
                      data-trip-list-group-target-placement="before"
                    >
                      <StopHeaderText>
                        <StopHeaderTitle variant="large" weight="bold" lineClamp={1}>
                          {card.stopHeader.title}
                        </StopHeaderTitle>
                        <StopHeaderSubtitle variant="medium">
                          <StopTimingEditControl
                            stop={card.stopHeader.stop}
                            tripStops={tripStops}
                            onSendMessage={onSendStopTimingEditMessage}
                            disabled={actionsDisabled}
                          >
                            {card.stopHeader.subtitle || addDaysOrDatesLabel}
                          </StopTimingEditControl>
                        </StopHeaderSubtitle>
                      </StopHeaderText>
                      {card.stopSearchActions && (
                        <StopHeaderActions>
                          <StopSearchActions
                            actions={card.stopSearchActions}
                            stop={card.stopHeader.stop}
                            variant="default"
                            onOpenStopHotels={onOpenStopHotels}
                            onOpenStopFlights={onOpenStopFlights}
                            onOpenStopExperiences={onOpenStopExperiences}
                          />
                        </StopHeaderActions>
                      )}
                    </StopHeader>
                  )}
                  {card.stopHeader && (
                    <StopDestinationRefinement variant="list" stop={card.stopHeader.stop} onSendMessage={onSendStopTimingEditMessage} disabled={actionsDisabled} />
                  )}
                  {card.dayGroups.map(group => (
                    <DayBody key={group.id}>
                      {group.rows.map((rowView, rowIndex, rowsInGroup) => {
                        const placement = rowView.row.id === overDragRowId ? dropTargetPlacement : undefined
                        // Only show the insertion line between two tiles, not at a group's
                        // top/bottom edge (before the first row or after the last row).
                        const isGroupEdge =
                          (placement === 'before' && rowIndex === 0) ||
                          (placement === 'after' && rowIndex === rowsInGroup.length - 1)
                        return (
                          <TripListRowItem
                            key={rowView.row.id}
                            row={rowView.row}
                            tripStops={tripStops}
                            selected={rowView.selected}
                            showGroupMetadata={rowView.showGroupMetadata}
                            showLocationMetadata={rowView.showLocationMetadata}
                            firstRow={rowView.firstRow}
                            lastRow={rowView.lastRow}
                            luxPlusEnabled={luxPlusEnabled}
                            onSelectRow={onSelectRow}
                            onRowClick={onRowClick}
                            onBookRow={onBookRow}
                            onViewBooking={onViewBooking}
                            onAskAi={onAskAi}
                            onAskAiMove={onAskAiMove}
                            onDelete={onDelete}
                            onMoveToDay={onMoveToDay}
                            onUpdateNote={onUpdateNote}
                            onUpdateStatus={onUpdateStatus}
                            isBookingPending={rowView.isBookingPending}
                            isNoteUpdatePending={rowView.isNoteUpdatePending}
                            isStatusUpdatePending={rowView.isStatusUpdatePending}
                            notesDisabled={notesDisabled}
                            statusDisabled={statusDisabled}
                            actionsDisabled={actionsDisabled}
                            dragDisabled={dragActionsDisabled}
                            dropTargetPlacement={isGroupEdge ? undefined : placement}
                          />
                        )
                      })}
                    </DayBody>
                  ))}
                  {card.emptyGroup && (
                    <EmptyStopPlaceholder
                      data-trip-list-group-target-id={card.emptyGroup.id}
                      data-trip-list-group-target-placement="after"
                    >
                      <BodyText variant="medium" colour="normal-contrast">
                        {intl.formatMessage({
                          id: 'dukav_e-eCPWiO_9pp-2',
                          defaultMessage: "You don't have anything planned in {placeName} yet",
                          description: 'Placeholder text for an empty stop in the trip planner list view',
                        }, {
                          placeName: card.emptyGroup.group.stopHeader?.title ?? card.emptyGroup.group.title,
                        })}
                      </BodyText>
                    </EmptyStopPlaceholder>
                  )}
                </StopCard>
              ))}
            </ContentStack>
            <DragOverlay
              dropAnimation={{
                duration: DROP_ANIMATION_DURATION,
                easing: 'ease-out',
                sideEffects: dropAnimationSideEffects,
              }}
              zIndex={zIndex.draggingItem}
            >
              {activeRenderableRow && (
                <DragOverlayContent>
                  <TripListRowItem
                    row={activeRenderableRow.row}
                    tripStops={tripStops}
                    selected={activeRenderableRow.selected}
                    showGroupMetadata={activeRenderableRow.showGroupMetadata}
                    showLocationMetadata={activeRenderableRow.showLocationMetadata}
                    firstRow={activeRenderableRow.firstRow}
                    lastRow={activeRenderableRow.lastRow}
                    luxPlusEnabled={luxPlusEnabled}
                    onSelectRow={onSelectRow}
                    onRowClick={onRowClick}
                    onBookRow={onBookRow}
                    onViewBooking={onViewBooking}
                    onAskAi={onAskAi}
                    onAskAiMove={onAskAiMove}
                    onDelete={onDelete}
                    onMoveToDay={onMoveToDay}
                    onUpdateNote={onUpdateNote}
                    onUpdateStatus={onUpdateStatus}
                    isBookingPending={activeRenderableRow.isBookingPending}
                    isNoteUpdatePending={activeRenderableRow.isNoteUpdatePending}
                    isStatusUpdatePending={activeRenderableRow.isStatusUpdatePending}
                    notesDisabled
                    statusDisabled
                    actionsDisabled
                    dragDisabled
                    isDragOverlay
                  />
                  <WhileDraggingStyle />
                </DragOverlayContent>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </ScrollArea>
      {selectedRows.length > 0 && (
        <TripListQuickActions
          selectedCount={selectedRows.length}
          disabled={actionsDisabled}
          moveDayOptions={selectedMoveDayOptions}
          showMoveWithAi={showSelectedMoveWithAi}
          onAskAi={onAskAiSelectedRows}
          onMoveToDay={onMoveSelectedRowsToDay}
          onMoveWithAi={onAskAiMoveSelectedRows}
          onDelete={onDeleteSelectedRows}
          onClear={onClearSelection}
        />
      )}
      {bookableRows.length > 0 && (
        <Footer>
          <FooterSummary direction="horizontal" gap={8} verticalAlign="center">
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: '3hjcizN16s5NKoi8v_PR',
                defaultMessage: 'Book through Luxury Escapes',
                description: 'Footer label describing rows that can be booked through Luxury Escapes',
              })}
            </BodyText>
            <CountChip>
              <Caption variant="large">
                {intl.formatMessage(
                  {
                    id: 'TtDyDrOLvud-zyDhBmK8',
                    defaultMessage: '{count, plural, one {# item} other {# items}}',
                    description: 'Footer count of bookable itinerary items in the trip planner list view',
                  },
                  { count: bookableRows.length },
                )}
              </Caption>
            </CountChip>
          </FooterSummary>
          <FooterActions direction="horizontal" gap={16} verticalAlign="center">
            {selectedRows.length > 0 && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'jkxrIQ9bs7jzZzZg3nm_',
                    defaultMessage: '{count, plural, one {# item selected} other {# items selected}}',
                    description: 'Selected item count shown in the trip planner list footer',
                  },
                  { count: selectedRows.length },
                )}
              </BodyText>
            )}
            {footerTotal && (
              <BodyText variant="medium" colour="normal-contrast">
                {intl.formatMessage(
                  {
                    id: '3dVgNkGJWa2aJcRksMWS',
                    defaultMessage: 'Total <amount>{amountValue}</amount>',
                    description: 'Total selected row price shown in the trip planner list footer',
                  },
                  {
                    amountValue: <FormatCurrency value={footerTotal.total} currency={footerTotal.currency} format="roundedDollar" />,
                    amount: renderTotalAmount,
                  },
                )}
              </BodyText>
            )}
            <BookAllButton size="medium" kind="primary" disabled={actionsDisabled} onClick={onBookAll}>
              {intl.formatMessage({
                id: 'pRxQObCdrNpxcYUl2ccY',
                defaultMessage: 'Book all',
                description: 'CTA button label for booking selected rows in the trip planner list view',
              })}
            </BookAllButton>
          </FooterActions>
        </Footer>
      )}
    </ListPanel>
  )
}

export default TripListView
